import React from 'react';
import { numFormat } from '@gatherstar/utils';
import { IconFont } from '../IconFont';

export interface StatisticItem {
  key?: string;
  label: string;
  value: number | string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  icon?: string;
  precision?: number;
  change?: number;
  isPercent?: boolean;
  config?: any;
  colorBySign?: boolean;
}

export interface StatisticCardsProps {
  data: StatisticItem[];
  className?: string;
  loading?: boolean;
  minWidth?: number;
  gap?: number;
}

/** 变化值 */
const renderChange = (value: number, isPercent = false) => {
  const color = value >= 0 ? 'var(--success)' : 'var(--error)';
  const prefix = value >= 0 ? '↑' : '↓';
  const abs = Math.abs(value);

  const formatted = isPercent ? `${prefix}${numFormat(abs * 100, 2)}%` : `${prefix}${numFormat(abs, 2)}`;

  return <span style={{ color }}>{formatted}</span>;
};

/** value 渲染 */
const renderValue = (value: number | string, precision = 2, loading?: boolean, colorBySign?: boolean) => {
  if (loading) {
    return <span className="inline-block h-[20px] w-[80px] rounded bg-gray-200 animate-pulse"/>;
  }

  if (typeof value === 'number') {
    const formatted = numFormat(value, precision);

    if (colorBySign) {
      const color = value >= 0 ? 'var(--success)' : 'var(--error)';
      const prefix = value > 0 ? '+' : '';
      return <span style={{ color: color }}>{prefix}{formatted}</span>;
    }

    return formatted;
  }

  return value;
};

const StatisticCards: React.FC<StatisticCardsProps> = ({
  data,
  className = '',
  loading = false,
  minWidth = 160,
  gap = 16,
}) => {
  return (
      <div
          className={className}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
            gap,
            width: '100%',
          }}
      >
        {data.map(item => (
            <div key={item.key || item.label} className="p-[16px] flex items-start">
              {/* icon */}
              {item.icon ? (
                  <div
                      className="flex-center w-[48px] h-[48px] rounded-full bg-gradient-to-b from-[#FFFFFF] to-[#F2F4F7] border-e4 text-[24px]">
                    <IconFont type={item.icon}/>
                  </div>
              ) : null}

              {/* content */}
              <div className="flex flex-col ml-[16px]">
                {/* label */}
                <span className="font-12 text-gray-500 mb-[4px]">{item.label}</span>

                {/* value */}
                <div className="flex items-center font-20 font-semibold text-[#111111]">
                  {item.prefix && <span className="mr-[4px] text-[14px] text-gray-500">{item.prefix}</span>}

                  {renderValue(item.value, item.precision, loading, item.colorBySign)}

                  {item.suffix && <span className="ml-[4px] text-[14px] text-gray-500">{item.suffix}</span>}
                </div>

                {/* change */}
                {item.change !== undefined && !loading && (
                    <div className="mt-[4px] font-12 text-gray-500">
                      <span className="mr-[4px]">环比</span>
                      {renderChange(item.change, item.isPercent ?? true)}
                    </div>
                )}
              </div>
            </div>
        ))}
      </div>
  );
};

export { StatisticCards };
